import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Popover,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { buildGuestLoginUrl } from '@/shared/auth/cognito';
import { useAuth } from '@/shared/auth/useAuth';
import { useAuthStore } from '@/shared/auth/useAuthStore';
import { GuestPageShell } from '@/shared/ui/GuestPageShell';
import { eventApi } from '@/entities/event/api/event.api';

import { requestApi } from '@/entities/request/api/request.api';
import type { SongRequest } from '@/entities/request/model/request.types';
import { spotifyApi } from '@/entities/spotify/api/spotify.api';
import type { SpotifyTrack } from '@/entities/spotify/model/spotify.types';
import { formatEventTime, formatRecurrenceDays, utcTimeToLocal } from '@/shared/lib/eventTime';

// ── shared icons & constants ───────────────────────────────────────────────

const iconPaths = {
  wave: 'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  music: 'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  location: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
  calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z',
  search: 'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z',
  up: 'M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z',
  arrow: 'M12 4l-1.41 1.41L15.17 10H4v2h11.17l-4.58 4.59L12 18l7-7-7-7z',
  send: 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2z',
  headphones: 'M12 3a8 8 0 0 0-8 8v6a3 3 0 0 0 3 3h2v-8H6v-1a6 6 0 0 1 12 0v1h-3v8h2a3 3 0 0 0 3-3v-6a8 8 0 0 0-8-8z',
  check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
  lock: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z',
  play: 'M8 5v14l11-7z',
  pause: 'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
  close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
};

const waveform = [22, 38, 16, 50, 30, 58, 20, 44, 34, 52, 24, 42, 28, 48, 18, 36];

const CARD_GRADIENTS = [
  'linear-gradient(135deg,#5B21B6,#DB2777)',
  'linear-gradient(135deg,#DB2777,#D97706)',
  'linear-gradient(135deg,#059669,#5B21B6)',
  'linear-gradient(135deg,#1DB954,#059669)',
  'linear-gradient(135deg,#D97706,#DC2626)',
  'linear-gradient(135deg,#7C3AED,#1DB954)',
];

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

// ── Dark-themed input ──────────────────────────────────────────────────────

function darkInputSx(secondaryMain: string, secondaryFocus: string, white: string) {
  return {
    '& .MuiOutlinedInput-root': {
      color: white,
      bgcolor: alpha(white, 0.07),
      borderRadius: 1,
      fontSize: 14,
      '& fieldset': { borderColor: alpha(white, 0.16) },
      '&:hover fieldset': { borderColor: alpha(white, 0.3) },
      '&.Mui-focused fieldset': { borderColor: secondaryMain },
    },
    '& input::placeholder': { color: alpha(white, 0.35), opacity: 1 },
    _secondaryFocus: secondaryFocus,
  };
}


// ── Demo event view (unchanged logic) ─────────────────────────────────────

const DEMO_EVENT = {
  name: 'Friday Night Sessions',
  venue: 'Electra, Cape Town',
  time: '21:00 – 02:00',
  nowPlaying: { title: 'Levitating', artist: 'Dua Lipa', gradient: 'linear-gradient(135deg, #5B21B6, #DB2777)' },
};

const DEMO_REQUESTS_INIT = [
  { id: '1', title: 'Blinding Lights', artist: 'The Weeknd', votes: 24, gradient: CARD_GRADIENTS[1] },
  { id: '2', title: 'As It Was', artist: 'Harry Styles', votes: 18, gradient: CARD_GRADIENTS[2] },
  { id: '3', title: 'Anti-Hero', artist: 'Taylor Swift', votes: 15, gradient: CARD_GRADIENTS[0] },
  { id: '4', title: 'Flowers', artist: 'Miley Cyrus', votes: 11, gradient: CARD_GRADIENTS[4] },
  { id: '5', title: 'Unholy', artist: 'Sam Smith', votes: 8, gradient: CARD_GRADIENTS[5] },
];

const SUGGESTIONS = ['Starboy', 'Watermelon Sugar', 'Heat Waves', 'Shivers', 'Stay'];

function DemoEventView({ guestName }: { guestName: string }) {
  const theme = useTheme();
  const [votes, setVotes] = useState(Object.fromEntries(DEMO_REQUESTS_INIT.map((r) => [r.id, r.votes])));
  const [myVotes, setMyVotes] = useState<Set<string>>(new Set());
  const [requestText, setRequestText] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  const handleVote = (id: string) => {
    setMyVotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); setVotes((v) => ({ ...v, [id]: v[id] - 1 })); }
      else { next.add(id); setVotes((v) => ({ ...v, [id]: v[id] + 1 })); }
      return next;
    });
  };

  const handleRequest = () => {
    if (!requestText.trim()) return;
    setSubmitted(requestText.trim());
    setRequestText('');
  };

  const sorted = [...DEMO_REQUESTS_INIT].sort((a, b) => (votes[b.id] ?? b.votes) - (votes[a.id] ?? a.votes));
  const inputSx = darkInputSx(theme.palette.secondary.main, theme.palette.secondary.light, theme.palette.common.white);

  return (
    <GuestPageShell eventName={DEMO_EVENT.name} eventCode="demo">
      <Stack spacing={3}>
        {/* Hero */}
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip label="Live" size="small" sx={{ height: 24, fontSize: 11, fontWeight: 900, color: '#10B981', bgcolor: 'rgba(16,185,129,0.13)', border: '1px solid rgba(16,185,129,0.3)', '& .MuiChip-label': { px: 1 } }} />
            <Chip label="Demo" size="small" sx={{ height: 24, fontSize: 11, fontWeight: 800, color: alpha(theme.palette.warning.light, 0.85), bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.light, 0.25)}`, '& .MuiChip-label': { px: 1 } }} />
          </Stack>
          <Typography sx={{ fontSize: { xs: 30, sm: 38 }, fontWeight: 900, color: 'common.white', lineHeight: 1.1, mb: 1 }}>{DEMO_EVENT.name}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <AppIcon path={iconPaths.location} sx={{ fontSize: 15, color: alpha(theme.palette.common.white, 0.35) }} />
            <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.5) }}>
              {DEMO_EVENT.venue}<Box component="span" sx={{ mx: 0.75, color: alpha(theme.palette.common.white, 0.25) }}>·</Box>{DEMO_EVENT.time}
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: alpha(theme.palette.common.white, 0.35) }}>
            Welcome back, <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.65), fontWeight: 700 }}>{guestName}</Box>
          </Typography>
        </Box>

        {/* Now Playing */}
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, bgcolor: alpha(theme.palette.common.white, 0.06), backdropFilter: 'blur(18px)' }}>
          <Typography sx={{ mb: 1.5, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.4) }}>Now Playing</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: 1.25, background: DEMO_EVENT.nowPlaying.gradient, display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.3)}` }}>
              <AppIcon path={iconPaths.music} sx={{ fontSize: 22, color: alpha('#fff', 0.85) }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 17, fontWeight: 900, color: 'common.white', lineHeight: 1.2 }}>{DEMO_EVENT.nowPlaying.title}</Typography>
              <Typography noWrap sx={{ mt: 0.3, fontSize: 13.5, color: alpha(theme.palette.common.white, 0.55) }}>{DEMO_EVENT.nowPlaying.artist}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="flex-end" spacing={0.4} sx={{ height: 36, mt: 1.75, opacity: 0.65 }}>
            {waveform.map((h, i) => (
              <Box key={i} sx={{ flex: '1 1 0', height: h * 0.55, minWidth: 3, borderRadius: 99, background: i % 4 === 0 ? `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.secondary.main})` : `linear-gradient(180deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})` }} />
            ))}
          </Stack>
        </Box>

        {/* Request */}
        <Box>
          <Typography sx={{ mb: 1.5, fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.45) }}>Request a Song</Typography>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth placeholder="Search for a song or artist…" value={requestText} onChange={(e) => setRequestText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRequest()} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><AppIcon path={iconPaths.search} sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.35) }} /></InputAdornment> }}
              sx={inputSx} />
            <Button onClick={handleRequest} variant="contained" sx={{ minWidth: 48, px: 1.5, bgcolor: 'secondary.main', flexShrink: 0, boxShadow: `0 8px 22px ${alpha(theme.palette.secondary.main, 0.34)}`, '&:hover': { bgcolor: 'secondary.dark' } }}>
              <AppIcon path={iconPaths.send} sx={{ fontSize: 18 }} />
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
            {SUGGESTIONS.map((s) => <Chip key={s} label={s} size="small" onClick={() => setRequestText(s)} sx={{ height: 26, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: alpha(theme.palette.common.white, 0.6), bgcolor: alpha(theme.palette.common.white, 0.07), border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`, '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.12), color: 'common.white' }, '& .MuiChip-label': { px: 1 } }} />)}
          </Stack>
          {submitted && (
            <Box sx={{ mt: 1.5, px: 1.75, py: 1.25, borderRadius: 1, border: '1px solid rgba(16,185,129,0.3)', bgcolor: 'rgba(16,185,129,0.08)' }}>
              <Typography sx={{ fontSize: 13, color: '#10B981', fontWeight: 700 }}>"{submitted}" added — <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.35), fontWeight: 400 }}>demo mode, not sent to DJ</Box></Typography>
            </Box>
          )}
        </Box>

        {/* Top requests */}
        <Box>
          <Typography sx={{ mb: 1.5, fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.45) }}>Top Requests</Typography>
          <Stack spacing={1}>
            {sorted.map((req, i) => {
              const voted = myVotes.has(req.id);
              const count = votes[req.id] ?? req.votes;
              return (
                <Box key={req.id} sx={{ display: 'grid', gridTemplateColumns: '6px auto minmax(0,1fr) auto', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1.5, borderRadius: 1.25, border: `1px solid ${i === 0 ? alpha(theme.palette.secondary.light, 0.3) : alpha(theme.palette.common.white, 0.08)}`, bgcolor: i === 0 ? alpha(theme.palette.secondary.main, 0.1) : alpha(theme.palette.common.white, 0.05), backdropFilter: 'blur(10px)' }}>
                  <Box sx={{ width: 4, height: 36, borderRadius: 99, background: req.gradient }} />
                  <Box sx={{ width: 40, height: 40, borderRadius: 1, background: req.gradient, display: 'grid', placeItems: 'center', flexShrink: 0 }}><AppIcon path={iconPaths.music} sx={{ fontSize: 16, color: alpha('#fff', 0.8) }} /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 800, color: 'common.white', lineHeight: 1.2 }}>{req.title}</Typography>
                    <Typography noWrap sx={{ mt: 0.25, fontSize: 12.5, color: alpha(theme.palette.common.white, 0.5) }}>{req.artist}</Typography>
                  </Box>
                  <Stack alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => handleVote(req.id)} sx={{ width: 32, height: 32, color: voted ? theme.palette.secondary.light : alpha(theme.palette.common.white, 0.35), bgcolor: voted ? alpha(theme.palette.secondary.main, 0.18) : alpha(theme.palette.common.white, 0.06), border: `1px solid ${voted ? alpha(theme.palette.secondary.light, 0.35) : alpha(theme.palette.common.white, 0.1)}`, transition: 'all 0.15s', '&:hover': { color: 'secondary.light', bgcolor: alpha(theme.palette.secondary.main, 0.18) } }}>
                      <AppIcon path={iconPaths.up} sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: voted ? 'secondary.light' : alpha(theme.palette.common.white, 0.4), lineHeight: 1 }}>{count}</Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </GuestPageShell>
  );
}

// ── Status chip ────────────────────────────────────────────────────────────

function statusChipSx(status: SongRequest['status']) {
  const map: Record<SongRequest['status'], { color: string; bg: string; border: string }> = {
    PENDING:   { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
    APPROVED:  { color: '#10B981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.3)'  },
    REJECTED:  { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)'   },
    PLAYED:    { color: '#818CF8', bg: 'rgba(129,140,248,0.12)', border: 'rgba(129,140,248,0.3)' },
    CANCELLED: { color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)' },
    SKIPPED:   { color: '#FF6B35', bg: 'rgba(255,107,53,0.16)',  border: 'rgba(255,107,53,0.42)' },
  };
  const c = map[status] ?? map.CANCELLED;
  return { height: 22, fontSize: 11, fontWeight: 700, color: c.color, bgcolor: c.bg, border: `1px solid ${c.border}`, '& .MuiChip-label': { px: 0.75 } };
}

// ── Live event view ────────────────────────────────────────────────────────

function LiveEventView({ eventCode, guestName }: { eventCode: string; guestName: string }) {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Event — polls every 3 s for status/requestStatus changes
  const { data: event, isLoading: eventLoading, error: eventQueryError } = useQuery({
    queryKey: ['public-event', eventCode],
    queryFn: () => eventApi.getPublicEvent(eventCode),
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
    retry: (failureCount, err: unknown) =>
      (err as { response?: { status?: number } })?.response?.status === 404 ? false : failureCount < 2,
  });

  // Playback — polls Spotify every 5 s independently of the DJ page
  const { data: playback } = useQuery({
    queryKey: ['public-event-playback', eventCode],
    queryFn: () => eventApi.getPublicEventPlayback(eventCode),
    enabled: !!eventCode && event?.status === 'ACTIVE',
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  });
  const eventError: 'not_found' | 'generic' | null = eventQueryError
    ? ((eventQueryError as { response?: { status?: number } })?.response?.status === 404 ? 'not_found' : 'generic')
    : null;

  // My requests — polls every 3 s so status changes appear quickly
  const { data: myRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['my-requests', eventCode],
    queryFn: () => requestApi.getMyRequests(eventCode),
    refetchInterval: 3000,
    refetchIntervalInBackground: false,
  });

  // Join the event once on mount so the DJ sees a guest session
  useEffect(() => {
    requestApi.joinEvent(eventCode).catch(() => {});
  }, [eventCode]);

  // Playlist drawer
  const PLAYLIST_PAGE_SIZE = 50;
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [playlistQuery, setPlaylistQuery] = useState('');
  const [playlistVisibleCount, setPlaylistVisibleCount] = useState(PLAYLIST_PAGE_SIZE);
  const { data: playlistTracks = [], isLoading: playlistLoading } = useQuery({
    queryKey: ['public-playlist', eventCode],
    queryFn: () => eventApi.getPublicPlaylist(eventCode),
    enabled: playlistOpen && !!event?.sharePlaylist,
    staleTime: 60_000,
  });

  // Search
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SpotifyTrack[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<SpotifyTrack | null>(null);
  const [selectedFromPlaylist, setSelectedFromPlaylist] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const REQ_PAGE_SIZE = 10;
  const [reqVisibleCount, setReqVisibleCount] = useState(REQ_PAGE_SIZE);
  const reqSentinelRef = useRef<HTMLDivElement | null>(null);
  const reqHasMoreRef = useRef(false);

  // Preview audio / embed
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [embedAnchor, setEmbedAnchor] = useState<HTMLElement | null>(null);
  const [embedTrackId, setEmbedTrackId] = useState<string | null>(null);

  const handlePreview = (e: React.MouseEvent, spotifyTrackId: string, previewUrl?: string | null) => {
    e.stopPropagation();
    if (previewUrl) {
      if (playingUrl === previewUrl) {
        audioRef.current?.pause();
        setPlayingUrl(null);
      } else {
        audioRef.current?.pause();
        audioRef.current = new Audio(previewUrl);
        audioRef.current.play();
        audioRef.current.onended = () => setPlayingUrl(null);
        setPlayingUrl(previewUrl);
      }
    } else {
      if (embedTrackId === spotifyTrackId) {
        setEmbedAnchor(null);
        setEmbedTrackId(null);
      } else {
        setEmbedTrackId(spotifyTrackId);
        setEmbedAnchor(e.currentTarget as HTMLElement);
      }
    }
  };

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      spotifyApi.searchTracks(eventCode, query)
        .then(setSearchResults)
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [query, eventCode]);

  useEffect(() => {
    const el = reqSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && reqHasMoreRef.current) setReqVisibleCount((c) => c + REQ_PAGE_SIZE); },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [myRequests.length]);

  useEffect(() => {
    setPlaylistVisibleCount(PLAYLIST_PAGE_SIZE);
  }, [playlistQuery, playlistOpen]);

  const filteredPlaylistTracks = useMemo(() => {
    const needle = playlistQuery.trim().toLowerCase();
    if (!needle) return playlistTracks;
    return playlistTracks.filter((track) =>
      [track.trackName, track.artistName, track.albumName]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(needle)),
    );
  }, [playlistTracks, playlistQuery]);

  const visiblePlaylistTracks = filteredPlaylistTracks.slice(0, playlistVisibleCount);
  const hasMorePlaylistTracks = filteredPlaylistTracks.length > visiblePlaylistTracks.length;

  const handleSelectTrack = (track: SpotifyTrack, fromPlaylist = false) => {
    setSelectedTrack(track);
    setSelectedFromPlaylist(fromPlaylist);
    setQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (!selectedTrack || submitting) return;
    setSubmitting(true);
    try {
      const req = await requestApi.submitRequest(eventCode, {
        trackName: selectedTrack.trackName,
        artistName: selectedTrack.artistName,
        albumName: selectedTrack.albumName,
        spotifyTrackId: selectedTrack.spotifyTrackId,
        spotifyUri: selectedTrack.spotifyUri,
        previewUrl: selectedTrack.previewUrl ?? undefined,
        explicit: selectedTrack.explicit,
      });
      queryClient.setQueryData<SongRequest[]>(['my-requests', eventCode], (prev) => [req, ...(prev ?? [])]);
      setSelectedTrack(null);
      setSelectedFromPlaylist(false);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      bgcolor: alpha(theme.palette.common.white, 0.08),
      borderRadius: 1.5,
      minHeight: 48,
      fontSize: 15,
      '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.16) },
      '&:hover fieldset': { borderColor: alpha(theme.palette.common.white, 0.3) },
      '&.Mui-focused fieldset': { borderColor: theme.palette.secondary.main },
    },
    '& input::placeholder': { color: alpha(theme.palette.common.white, 0.35), opacity: 1 },
  };

  const glassCard = {
    position: 'relative',
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
    bgcolor: alpha(theme.palette.common.white, 0.065),
    borderRadius: 2,
    backdropFilter: 'blur(20px)',
    p: { xs: 2, sm: 2.5 },
    boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.18)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${theme.palette.warning.light}, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
      opacity: 0.82,
    },
  };

  // ── Loading ──
  if (eventLoading) {
    const sk = (w: string | number, h: number) => (
      <Skeleton variant="rounded" width={w} height={h} sx={{ bgcolor: alpha(theme.palette.common.white, 0.07), borderRadius: 1 }} />
    );
    return (
      <GuestPageShell eventCode={eventCode}>
        <Stack spacing={3}>
          <Stack spacing={1}>{sk('50%', 20)}{sk('75%', 38)}{sk('45%', 16)}</Stack>
          {sk('100%', 80)}
          {sk('100%', 52)}
          <Stack spacing={1}>{sk('100%', 64)}{sk('100%', 64)}{sk('100%', 64)}</Stack>
        </Stack>
      </GuestPageShell>
    );
  }

  // ── Not found ──
  if (eventError === 'not_found' || !event) {
    return (
      <GuestPageShell eventCode={eventCode}>
        <Stack alignItems="center" spacing={2} sx={{ pt: 8, textAlign: 'center' }}>
          <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: alpha(theme.palette.common.white, 0.06), border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, display: 'grid', placeItems: 'center' }}>
            <AppIcon path={iconPaths.wave} sx={{ fontSize: 28, color: alpha(theme.palette.common.white, 0.25) }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: alpha(theme.palette.common.white, 0.65) }}>Event not found</Typography>
          <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.4) }}>Check the link and try again, or ask the DJ for a new one.</Typography>
        </Stack>
      </GuestPageShell>
    );
  }

  const statusLabel =
    event.status === 'ACTIVE'  ? 'Live' :
    event.status === 'PENDING' ? 'Up & Coming' :
    event.status === 'CLOSED'  ? 'Closed' : event.status;
  const statusColor =
    event.status === 'ACTIVE'  ? '#10B981' :
    event.status === 'PENDING' ? '#818CF8' :
    event.status === 'CLOSED'  ? '#EF4444' : alpha(theme.palette.common.white, 0.4);
  const statusBg =
    event.status === 'ACTIVE'  ? 'rgba(16,185,129,0.13)'  :
    event.status === 'PENDING' ? 'rgba(129,140,248,0.13)' :
    event.status === 'CLOSED'  ? 'rgba(239,68,68,0.13)'   : alpha(theme.palette.common.white, 0.07);
  const statusBorder =
    event.status === 'ACTIVE'  ? 'rgba(16,185,129,0.3)'  :
    event.status === 'PENDING' ? 'rgba(129,140,248,0.3)' :
    event.status === 'CLOSED'  ? 'rgba(239,68,68,0.3)'   : alpha(theme.palette.common.white, 0.14);

  function formatDate(d?: string) {
    if (!d) return null;
    return new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'long' });
  }

  function nextRecurrenceDateStr(recurrenceDays: number[], startUtc?: string): string {
    const now = new Date();
    const todayDow = now.getUTCDay();
    const nowMins = now.getUTCHours() * 60 + now.getUTCMinutes();
    const [sh = 0, sm = 0] = (startUtc ?? '').split(':').map(Number);
    const startMins = sh * 60 + sm;
    const sorted = [...recurrenceDays].sort((a, b) => a - b);
    // If today is a recurrence day and event hasn't started yet, show today
    if (sorted.includes(todayDow) && nowMins < startMins) {
      return now.toISOString().slice(0, 10);
    }
    const futureDow = sorted.find(d => d > todayDow);
    const daysUntil = futureDow !== undefined ? futureDow - todayDow : 7 - todayDow + sorted[0];
    const next = new Date(now);
    next.setUTCDate(now.getUTCDate() + daysUntil);
    return next.toISOString().slice(0, 10);
  }

  function eventDateLabel(): { line: string; badge?: string } | null {
    if (!event) return null;
    const { status, eventDate, startTime, endTime, recurrence, recurrenceDays } = event;
    if (status === 'ARCHIVED') return null;
    const isRecurring = recurrence === 'weekly' && recurrenceDays?.length;
    const days = isRecurring ? `Every ${formatRecurrenceDays(recurrenceDays!)}` : null;

    if (status === 'ACTIVE') {
      if (!endTime) return days ? { line: days } : null;
      const endStr = utcTimeToLocal(eventDate ?? new Date().toISOString().slice(0, 10), endTime);
      return { line: [days, `Ends at ${endStr}`].filter(Boolean).join(' · ') };
    }

    if (status === 'PENDING') {
      if (isRecurring) {
        const nextDate = nextRecurrenceDateStr(recurrenceDays!, startTime);
        const nextFormatted = formatDate(nextDate) ?? nextDate;
        const timeStr = startTime ? ` at ${utcTimeToLocal(nextDate, startTime)}` : '';
        return {
          line: days!,
          badge: `Next: ${nextFormatted}${timeStr}`,
        };
      }
      if (!eventDate) return null;
      const timeRange = formatEventTime(eventDate, startTime, endTime);
      return {
        line: `${formatDate(eventDate) ?? eventDate}${timeRange ? ` · ${timeRange}` : ''}`,
        badge: `Starts ${formatDate(eventDate)}${startTime ? ` at ${utcTimeToLocal(eventDate, startTime)}` : ''}`,
      };
    }

    if (status === 'CLOSED') {
      const date = eventDate ?? new Date().toISOString().slice(0, 10);
      if (!endTime) return days ? { line: days } : null;
      const endStr = utcTimeToLocal(date, endTime);
      return { line: [days, `Ended ${isRecurring ? '' : (formatDate(date) ?? date) + ' '}at ${endStr}`.trim()].filter(Boolean).join(' · ') };
    }

    return null;
  }

  return (
    <GuestPageShell eventName={event.name} eventCode={eventCode}>
      <Stack spacing={3}>

        {/* Header */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.13)}`,
            bgcolor: alpha(theme.palette.common.white, 0.065),
            backdropFilter: 'blur(22px)',
            boxShadow: `0 24px 58px ${alpha(theme.palette.common.black, 0.22)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -70,
              right: -120,
              width: 260,
              height: 190,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(theme.palette.secondary.light, 0.22)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.warning.light, 0.16)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.success.light}, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
              opacity: 0.75,
            },
          }}
        >
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ position: 'relative', mb: 1.5 }}>
            <Chip label={statusLabel} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 900, color: statusColor, bgcolor: statusBg, border: `1px solid ${statusBorder}`, '& .MuiChip-label': { px: 1 } }} />
            {(() => {
              const notPlaying = event.status === 'ACTIVE' && playback?.isPlaying === false;
              const label = notPlaying
                ? 'Playlist not playing'
                : event.requestStatus === 'OPEN'
                  ? 'Requests open'
                  : event.requestStatus === 'PAUSED'
                    ? 'Requests paused'
                    : 'Requests closed';
              const isOpen = !notPlaying && event.requestStatus === 'OPEN';
              const isPaused = notPlaying || event.requestStatus === 'PAUSED';
              return (
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    height: 24, fontSize: 11, fontWeight: 700,
                    color: isOpen ? '#10B981' : isPaused ? alpha(theme.palette.warning.light, 0.85) : alpha(theme.palette.common.white, 0.4),
                    bgcolor: isOpen ? 'rgba(16,185,129,0.1)' : isPaused ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.common.white, 0.06),
                    border: `1px solid ${isOpen ? 'rgba(16,185,129,0.28)' : isPaused ? alpha(theme.palette.warning.light, 0.25) : alpha(theme.palette.common.white, 0.12)}`,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              );
            })()}
          </Stack>

          <Typography sx={{ position: 'relative', fontSize: { xs: 34, sm: 42 }, fontWeight: 900, color: 'common.white', lineHeight: { xs: 0.98, sm: 1.02 }, mb: 1.35, textWrap: 'balance' }}>
            {event.name}
          </Typography>

          <Stack spacing={0.75} sx={{ position: 'relative' }}>
            {event.venueName && (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                <AppIcon path={iconPaths.location} sx={{ fontSize: 16, color: alpha(theme.palette.secondary.light, 0.75), flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.68), fontWeight: 650 }}>{event.venueName}</Typography>
              </Stack>
            )}
            {(() => {
              const label = eventDateLabel();
              if (!label) return null;
              return (
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                  <AppIcon path={iconPaths.calendar} sx={{ fontSize: 16, color: alpha(theme.palette.warning.light, 0.75), flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.62), lineHeight: 1.35 }}>{label.line}</Typography>
                </Stack>
              );
            })()}
          </Stack>

          {(() => {
            const label = eventDateLabel();
            if (!label?.badge) return null;
            return (
              <Box sx={{ position: 'relative', mt: 1.25, px: 1.5, py: 1, borderRadius: 99, bgcolor: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.25)', display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <AppIcon path={iconPaths.calendar} sx={{ fontSize: 14, color: '#818CF8' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#818CF8' }}>{label.badge}</Typography>
              </Box>
            );
          })()}

          <Typography sx={{ position: 'relative', mt: 1.35, fontSize: 13, color: alpha(theme.palette.common.white, 0.42) }}>
            Welcome, <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.65), fontWeight: 700 }}>{guestName}</Box>
          </Typography>
        </Box>

        {/* Request section */}
        {event.status === 'PENDING' ? (
          <Box sx={{ ...glassCard, textAlign: 'center', py: 4 }}>
            <AppIcon path={iconPaths.calendar} sx={{ fontSize: 32, color: 'rgba(129,140,248,0.5)', mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'rgba(129,140,248,0.9)' }}>This event hasn't started yet</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: alpha(theme.palette.common.white, 0.4) }}>Requests will open once the event goes live.</Typography>
          </Box>
        ) : event.status === 'CLOSED' ? (
          <Box sx={{ ...glassCard, textAlign: 'center', py: 4 }}>
            <AppIcon path={iconPaths.lock} sx={{ fontSize: 32, color: 'rgba(239,68,68,0.4)', mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'rgba(239,68,68,0.85)' }}>This event has ended</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: alpha(theme.palette.common.white, 0.35) }}>Song requests are no longer being accepted.</Typography>
          </Box>
        ) : event.requestStatus !== 'OPEN' ? (
          <Box sx={{ ...glassCard, textAlign: 'center', py: 4 }}>
            <AppIcon path={iconPaths.lock} sx={{ fontSize: 32, color: alpha(theme.palette.warning.light, 0.45), mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: alpha(theme.palette.warning.light, 0.8) }}>Requests are paused</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: alpha(theme.palette.common.white, 0.4) }}>The DJ has temporarily paused requests. Check back shortly.</Typography>
          </Box>
        ) : event.status === 'ACTIVE' && playback?.isPlaying === false ? (
          <Box sx={{ ...glassCard, textAlign: 'center', py: 4 }}>
            <AppIcon path={iconPaths.music} sx={{ fontSize: 32, color: alpha(theme.palette.warning.light, 0.45), mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: alpha(theme.palette.warning.light, 0.8) }}>Playlist not playing</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: alpha(theme.palette.common.white, 0.4) }}>Requests will open once the DJ starts the playlist.</Typography>
          </Box>
        ) : (
          <Box sx={glassCard}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative', mb: 1.5 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '34% 46% 36% 44%', display: 'grid', placeItems: 'center', color: 'common.white', bgcolor: event.sharePlaylist ? 'rgba(16,185,129,0.16)' : alpha(theme.palette.secondary.main, 0.22), border: `1px solid ${event.sharePlaylist ? 'rgba(16,185,129,0.28)' : alpha(theme.palette.secondary.light, 0.28)}`, transform: 'rotate(-4deg)' }}>
                <AppIcon path={iconPaths.music} sx={{ fontSize: 17 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'common.white', lineHeight: 1.05 }}>
                  {event.sharePlaylist ? 'Pick from the DJ playlist' : 'Request a song'}
                </Typography>
                <Typography sx={{ mt: 0.3, fontSize: 12.5, fontWeight: 600, color: alpha(theme.palette.common.white, 0.42), lineHeight: 1.2 }}>
                  {event.sharePlaylist ? 'Playlist picks are the fastest way into the set' : 'Search Spotify and send it to the DJ'}
                </Typography>
              </Box>
            </Stack>

            {/* Search field */}
            {!selectedTrack && (
              <>
                {event?.sharePlaylist && (
                  <Box sx={{ mb: 1.35 }}>
                    <Button
                      fullWidth
                      onClick={() => setPlaylistOpen(true)}
                      startIcon={<AppIcon path={iconPaths.check} sx={{ fontSize: 18 }} />}
                      sx={{
                        minHeight: 54,
                        justifyContent: 'flex-start',
                        px: 1.5,
                        textAlign: 'left',
                        fontSize: 15,
                        fontWeight: 900,
                        textTransform: 'none',
                        color: 'common.white',
                        border: '1px solid rgba(16,185,129,0.36)',
                        borderRadius: 1.5,
                        bgcolor: 'rgba(16,185,129,0.12)',
                        boxShadow: `0 14px 30px ${alpha(theme.palette.success.main, 0.12)}`,
                        '&:hover': { borderColor: 'rgba(16,185,129,0.58)', bgcolor: 'rgba(16,185,129,0.18)' },
                        '& .MuiButton-startIcon': { color: '#10B981', mr: 1 },
                      }}
                    >
                      Browse DJ playlist
                    </Button>
                    <Typography sx={{ mt: 0.7, fontSize: 12.5, fontWeight: 650, color: alpha(theme.palette.common.white, 0.42), lineHeight: 1.35 }}>
                      Songs from the playlist are instantly approved. Use search only if your song is not there.
                    </Typography>
                  </Box>
                )}
                {event?.sharePlaylist && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.15 }}>
                    <Box sx={{ height: 1, flex: 1, bgcolor: alpha(theme.palette.common.white, 0.1) }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.32), whiteSpace: 'nowrap' }}>
                      Not on the playlist?
                    </Typography>
                    <Box sx={{ height: 1, flex: 1, bgcolor: alpha(theme.palette.common.white, 0.1) }} />
                  </Stack>
                )}
                <TextField
                  fullWidth
                  placeholder={event.sharePlaylist ? 'Search Spotify as a backup…' : 'Search for a song or artist…'}
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSubmitSuccess(false); setSelectedFromPlaylist(false); }}
                  size="small"
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {searching
                          ? <CircularProgress size={16} sx={{ color: alpha(theme.palette.common.white, 0.4) }} />
                          : <AppIcon path={iconPaths.search} sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.35) }} />}
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
              </>
            )}

            {/* Search results */}
            {!selectedTrack && searchResults.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {searchResults.map((track) => (
                  <Box
                    key={track.spotifyTrackId}
                    onClick={() => handleSelectTrack(track)}
                    sx={{
                      px: 1.5, py: 1.35, borderRadius: 1.5, cursor: 'pointer',
                      minHeight: 62,
                      border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                      bgcolor: alpha(theme.palette.common.white, 0.052),
                      '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.13), borderColor: alpha(theme.palette.secondary.light, 0.3) },
                      transition: 'all 0.15s',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: 'common.white', lineHeight: 1.2 }}>
                          {track.trackName}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.5) }}>
                          {track.artistName}
                          {track.albumName && (
                            <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.3) }}> · {track.albumName}</Box>
                          )}
                        </Typography>
                      </Box>
                      {track.spotifyTrackId && (() => {
                        const isEmbed = embedTrackId === track.spotifyTrackId;
                        const isAudio = !!track.previewUrl && playingUrl === track.previewUrl;
                        const active = isEmbed || isAudio;
                        return (
                          <Tooltip title="Preview" placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => handlePreview(e, track.spotifyTrackId, track.previewUrl)}
                              sx={{
                                flexShrink: 0, width: 30, height: 30,
                                color: active ? theme.palette.secondary.light : alpha(theme.palette.common.white, 0.5),
                                bgcolor: active ? alpha(theme.palette.secondary.main, 0.2) : 'transparent',
                                border: `1px solid ${active ? alpha(theme.palette.secondary.light, 0.4) : alpha(theme.palette.common.white, 0.12)}`,
                                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.light },
                              }}
                            >
                              <AppIcon path={isAudio ? iconPaths.pause : isEmbed ? iconPaths.close : iconPaths.play} sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Selected track confirmation */}
            {selectedTrack && !submitSuccess && (
              <Box sx={{ position: 'relative', border: `1px solid ${alpha(theme.palette.secondary.light, 0.28)}`, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 1.5, px: 1.5, py: 1.35 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 800, color: 'common.white' }}>{selectedTrack.trackName}</Typography>
                    <Typography noWrap sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.5) }}>{selectedTrack.artistName}</Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => { setSelectedTrack(null); setSelectedFromPlaylist(false); }}
                    sx={{ flexShrink: 0, fontSize: 12, color: alpha(theme.palette.common.white, 0.4), minWidth: 0, px: 0.75 }}
                  >
                    Change
                  </Button>
                </Stack>
                <Typography sx={{ mt: 0.85, fontSize: 12.5, color: selectedFromPlaylist ? '#10B981' : alpha(theme.palette.common.white, 0.42), fontWeight: selectedFromPlaylist ? 750 : 600 }}>
                  {selectedFromPlaylist ? 'Playlist pick: this can be approved instantly.' : 'Backup request: the DJ still needs to approve it.'}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{ mt: 1.5, minHeight: 46, borderRadius: 1.5, bgcolor: 'secondary.main', boxShadow: `0 12px 28px ${alpha(theme.palette.secondary.main, 0.34)}`, '&:hover': { bgcolor: 'secondary.dark' }, '&.Mui-disabled': { bgcolor: alpha(theme.palette.secondary.main, 0.35) } }}
                >
                  {submitting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : selectedFromPlaylist ? 'Send playlist pick' : 'Send backup request'}
                </Button>
              </Box>
            )}

            {/* Success */}
            {submitSuccess && (
              <Box sx={{ mt: 0.5, px: 1.75, py: 1.35, borderRadius: 1.5, border: '1px solid rgba(16,185,129,0.3)', bgcolor: 'rgba(16,185,129,0.08)' }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AppIcon path={iconPaths.check} sx={{ fontSize: 16, color: '#10B981' }} />
                  <Typography sx={{ fontSize: 13, color: '#10B981', fontWeight: 700 }}>Request sent to the DJ!</Typography>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* My requests */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(theme.palette.common.white, 0.52) }}>
              My requests
            </Typography>
            {!requestsLoading && myRequests.length > 0 && (
              <Chip
                label={myRequests.length}
                size="small"
                sx={{ height: 22, minWidth: 26, fontSize: 11, fontWeight: 900, color: 'secondary.light', bgcolor: alpha(theme.palette.secondary.main, 0.12), border: `1px solid ${alpha(theme.palette.secondary.light, 0.24)}`, '& .MuiChip-label': { px: 0.8 } }}
              />
            )}
          </Stack>
          {requestsLoading ? (
            <Stack spacing={1}>
              {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={60} sx={{ bgcolor: alpha(theme.palette.common.white, 0.07), borderRadius: 1.25 }} />)}
            </Stack>
          ) : myRequests.length === 0 ? (
            <Box sx={{ py: 4, px: 2, textAlign: 'center', borderRadius: 2, border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`, bgcolor: alpha(theme.palette.common.white, 0.035), backdropFilter: 'blur(12px)' }}>
              <Box sx={{ width: 42, height: 42, borderRadius: '34% 46% 36% 44%', display: 'grid', placeItems: 'center', mx: 'auto', mb: 1.25, color: alpha(theme.palette.common.white, 0.5), bgcolor: alpha(theme.palette.common.white, 0.055), border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`, transform: 'rotate(-4deg)' }}>
                <AppIcon path={iconPaths.music} sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: alpha(theme.palette.common.white, 0.5) }}>No requests yet</Typography>
              <Typography sx={{ mt: 0.35, fontSize: 12.5, color: alpha(theme.palette.common.white, 0.32) }}>Find a track above and send it to the DJ.</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {(() => {
                const sortedReqs = [...myRequests].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                reqHasMoreRef.current = sortedReqs.length > reqVisibleCount;
                return sortedReqs.slice(0, reqVisibleCount);
              })().map((req) => (
                <Box
                  key={req.requestId}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    px: { xs: 1.5, sm: 2 }, py: 1.35, borderRadius: 1.5,
                    border: `1px solid ${req.status === 'REJECTED' ? 'rgba(239,68,68,0.22)' : alpha(theme.palette.common.white, 0.08)}`,
                    bgcolor: req.status === 'REJECTED' ? 'rgba(239,68,68,0.05)' : alpha(theme.palette.common.white, 0.052),
                    backdropFilter: 'blur(14px)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background:
                        req.status === 'REJECTED'
                          ? 'rgba(239,68,68,0.7)'
                          : req.status === 'APPROVED'
                            ? `linear-gradient(180deg, ${theme.palette.success.light}, ${theme.palette.secondary.light})`
                            : `linear-gradient(180deg, ${theme.palette.warning.light}, ${theme.palette.primary.light})`,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.25 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: 'common.white', lineHeight: 1.2 }}>
                        {req.trackName}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.5) }}>
                        {req.artistName}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="flex-end" spacing={0.65} sx={{ flexShrink: 0 }}>
                      {req.spotifyTrackId && (() => {
                        const isEmbed = embedTrackId === req.spotifyTrackId;
                        const isAudio = !!req.previewUrl && playingUrl === req.previewUrl;
                        const active = isEmbed || isAudio;
                        return (
                          <Tooltip title="Preview" placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => handlePreview(e, req.spotifyTrackId!, req.previewUrl)}
                              sx={{
                                width: 28, height: 28,
                                color: active ? theme.palette.secondary.light : alpha(theme.palette.common.white, 0.4),
                                bgcolor: active ? alpha(theme.palette.secondary.main, 0.2) : 'transparent',
                                border: `1px solid ${active ? alpha(theme.palette.secondary.light, 0.4) : alpha(theme.palette.common.white, 0.1)}`,
                                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.15), color: theme.palette.secondary.light },
                              }}
                            >
                              <AppIcon path={isAudio ? iconPaths.pause : isEmbed ? iconPaths.close : iconPaths.play} sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                      <Chip
                        label={
                          req.status === 'PLAYED' && req.playedAt
                            ? `Played ${new Date(req.playedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : req.status === 'SKIPPED'
                              ? 'SKIPPED'
                              : req.status === 'APPROVED' && (req.queuePosition === 0 || (event && req.spotifyTrackId && playback?.currentlyPlayingTrackId === req.spotifyTrackId))
                                ? 'Now playing'
                                : req.status === 'APPROVED' && req.queuePosition
                                  ? `In ${req.queuePosition} song${req.queuePosition === 1 ? '' : 's'}`
                                  : req.status
                        }
                        size="small"
                        sx={statusChipSx(req.status)}
                      />
                    </Stack>
                  </Box>
                  {req.status === 'REJECTED' && req.rejectionReason && (
                    <Typography sx={{ mt: 0.75, fontSize: 12, color: 'rgba(239,68,68,0.75)', lineHeight: 1.5 }}>
                      {req.rejectionReason}
                    </Typography>
                  )}
                </Box>
              ))}
              <Box ref={reqSentinelRef} sx={{ height: 1 }} />
            </Stack>
          )}
        </Box>

      </Stack>

      <Popover
        open={!!embedAnchor}
        anchorEl={embedAnchor}
        onClose={() => { setEmbedAnchor(null); setEmbedTrackId(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } } }}
      >
        {embedTrackId && (
          <Box component="iframe"
            src={`https://open.spotify.com/embed/track/${embedTrackId}?utm_source=generator&theme=0&autoplay=1`}
            width={300}
            height={80}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            sx={{ border: 'none', borderRadius: 1.5, display: 'block' }}
          />
        )}
      </Popover>

      {/* Playlist drawer — only rendered when sharePlaylist is on */}
      <Drawer
        anchor="bottom"
        open={playlistOpen}
        onClose={() => setPlaylistOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#0F0A1E',
            backgroundImage: `
              linear-gradient(155deg, ${alpha(theme.palette.primary.dark, 0.36)} 0%, transparent 42%),
              radial-gradient(circle at 88% 0%, ${alpha(theme.palette.secondary.dark, 0.34)} 0%, transparent 36%)
            `,
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
            maxHeight: { xs: '88vh', sm: '85vh' },
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            borderBottom: 'none',
            boxShadow: `0 -24px 70px ${alpha(theme.palette.common.black, 0.42)}`,
          },
        }}
      >
        {/* Handle + header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 0 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: alpha(theme.palette.common.white, 0.15), mx: 'auto', mb: 2.5 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 900, color: 'common.white', lineHeight: 1.2 }}>DJ's Playlist</Typography>
              <Typography sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.4), mt: 0.25 }}>
                Songs from the playlist are instantly approved
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setPlaylistOpen(false)}
              sx={{ color: alpha(theme.palette.common.white, 0.4), '&:hover': { color: 'common.white' } }}>
              <AppIcon path={iconPaths.close} sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
          <TextField
            fullWidth
            placeholder="Filter playlist by song or artist..."
            value={playlistQuery}
            onChange={(e) => setPlaylistQuery(e.target.value)}
            size="small"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AppIcon path={iconPaths.search} sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.35) }} />
                </InputAdornment>
              ),
              endAdornment: playlistQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setPlaylistQuery('')}
                    sx={{ width: 28, height: 28, color: alpha(theme.palette.common.white, 0.4), '&:hover': { color: 'common.white' } }}
                  >
                    <AppIcon path={iconPaths.close} sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ ...inputSx, mt: 1.75 }}
          />
          {!playlistLoading && playlistTracks.length > 0 && (
            <Typography sx={{ mt: 0.85, fontSize: 12, fontWeight: 650, color: alpha(theme.palette.common.white, 0.34) }}>
              Showing {visiblePlaylistTracks.length} of {filteredPlaylistTracks.length}
              {playlistQuery ? ` matching ${filteredPlaylistTracks.length === 1 ? 'track' : 'tracks'}` : ` playlist ${playlistTracks.length === 1 ? 'track' : 'tracks'}`}
            </Typography>
          )}
        </Box>

        {/* Track list */}
        <Box sx={{ overflow: 'auto', px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 4 }}>
          {playlistLoading ? (
            <Stack spacing={1}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={62} sx={{ bgcolor: alpha(theme.palette.common.white, 0.07), borderRadius: 1.25 }} />
              ))}
            </Stack>
          ) : playlistTracks.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.35) }}>No tracks in playlist</Typography>
            </Box>
          ) : filteredPlaylistTracks.length === 0 ? (
            <Box sx={{ py: 5, px: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: alpha(theme.palette.common.white, 0.48) }}>No playlist matches</Typography>
              <Typography sx={{ mt: 0.45, fontSize: 12.5, color: alpha(theme.palette.common.white, 0.34) }}>Try a different song or artist.</Typography>
            </Box>
          ) : (
            <Stack spacing={0.75}>
              {visiblePlaylistTracks.map((track) => {
                const myReq = myRequests.find(
                  (r) => r.spotifyTrackId === track.spotifyTrackId && r.status !== 'REJECTED' && r.status !== 'CANCELLED',
                );
                const isCurrentlyPlaying = !!playback?.currentlyPlayingTrackId && track.spotifyTrackId === playback.currentlyPlayingTrackId;
                return (
                  <Box
                    key={track.spotifyTrackId}
                    onClick={() => {
                      if (!myReq) {
                        handleSelectTrack(track, true);
                        setPlaylistOpen(false);
                      }
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 1.5, py: 1.1, borderRadius: 1.5,
                      minHeight: 66,
                      border: `1px solid ${isCurrentlyPlaying ? 'rgba(16,185,129,0.3)' : myReq ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.white, 0.08)}`,
                      bgcolor: isCurrentlyPlaying ? 'rgba(16,185,129,0.06)' : myReq ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.white, 0.05),
                      cursor: myReq ? 'default' : 'pointer',
                      opacity: myReq && !isCurrentlyPlaying ? 0.7 : 1,
                      '&:hover': myReq ? {} : { bgcolor: alpha(theme.palette.secondary.main, 0.12), borderColor: alpha(theme.palette.secondary.light, 0.25) },
                      transition: 'all 0.15s',
                    }}
                  >
                    {track.imageUrl ? (
                      <Box component="img" src={track.imageUrl} alt="" sx={{ width: 44, height: 44, borderRadius: 0.75, flexShrink: 0, objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ width: 44, height: 44, borderRadius: 0.75, flexShrink: 0, bgcolor: alpha(theme.palette.secondary.main, 0.2), display: 'grid', placeItems: 'center' }}>
                        <AppIcon path={iconPaths.music} sx={{ fontSize: 18, color: alpha(theme.palette.common.white, 0.5) }} />
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: 'common.white', lineHeight: 1.2 }}>{track.trackName}</Typography>
                      <Typography noWrap sx={{ fontSize: 12.5, color: alpha(theme.palette.common.white, 0.5) }}>{track.artistName}</Typography>
                    </Box>
                    {isCurrentlyPlaying ? (
                      <Chip label="Now playing" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, color: '#10B981', bgcolor: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', '& .MuiChip-label': { px: 0.75 } }} />
                    ) : myReq ? (
                      <Chip label={myReq.status === 'APPROVED' ? (myReq.queuePosition != null && myReq.queuePosition > 0 ? `In ${myReq.queuePosition}` : 'Queued') : myReq.status} size="small" sx={statusChipSx(myReq.status)} />
                    ) : (
                      <Button size="small" variant="outlined" sx={{ flexShrink: 0, fontSize: 11, fontWeight: 800, textTransform: 'none', minWidth: 0, px: 1.25, py: 0.4, height: 30, borderRadius: 99, color: theme.palette.secondary.light, borderColor: alpha(theme.palette.secondary.light, 0.35), bgcolor: alpha(theme.palette.secondary.main, 0.06), '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.15), borderColor: theme.palette.secondary.light } }}>
                        Request
                      </Button>
                    )}
                  </Box>
                );
              })}
              {hasMorePlaylistTracks && (
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setPlaylistVisibleCount((count) => count + PLAYLIST_PAGE_SIZE)}
                  sx={{
                    mt: 0.75,
                    minHeight: 40,
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: alpha(theme.palette.common.white, 0.72),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.common.white, 0.05),
                    '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.09) },
                  }}
                >
                  Show {Math.min(PLAYLIST_PAGE_SIZE, filteredPlaylistTracks.length - visiblePlaylistTracks.length)} more
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>

    </GuestPageShell>
  );
}

// ── Router component ───────────────────────────────────────────────────────

export function GuestEventPage() {
  const { eventCode } = useParams<{ eventCode: string }>();
  const theme = useTheme();
  const { user } = useAuth();
  const hydrated = useAuthStore((s) => s._hydrated);

  useEffect(() => {
    if (!hydrated) return;
    if (!user) {
      window.location.href = buildGuestLoginUrl(`/events/${eventCode}`);
    }
  }, [hydrated, user, eventCode]);

  if (!hydrated || !user) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundImage: `linear-gradient(120deg, ${alpha(theme.palette.primary.dark, 0.98)} 0%, ${alpha(theme.palette.text.primary, 0.95)} 52%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%), repeating-linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.07)} 0 1px, transparent 1px 18px)`,
      }}>
        <CircularProgress sx={{ color: 'secondary.light' }} />
      </Box>
    );
  }

  const guestName = user.firstName ?? user.displayName?.split(' ')[0] ?? 'Guest';

  if (eventCode === 'demo') return <DemoEventView guestName={guestName} />;

  return <LiveEventView eventCode={eventCode!} guestName={guestName} />;
}
